import { useSearch, useNavigate } from '@tanstack/react-router'
import { T, CAT_LABELS, catColor } from '../lib/tokens'
import { useItems, useCategories, useCategoryCounts } from '../lib/hooks'
import { Sidebar } from '../components/Sidebar'
import { Topbar } from '../components/Topbar'
import { MosaicCard } from '../components/MosaicCard'
import { GeminiDrawer, useGeminiDrawer } from '../components/GeminiDrawer'
import { LoadingSpinner, ErrorMessage, CatDot, TimeAgo } from '../components/ui'

export function DashboardPage() {
  // カテゴリ/お気に入りの絞り込みは URL を単一ソースにする（サイドバーと自動同期・共有/リロード可）。
  const search = useSearch({ strict: false }) as { cat?: string; fav?: string }
  const navigate = useNavigate()
  const activeCat = search.cat ?? null
  const favOnly = search.fav === '1'
  const { open: geminiOpen, setOpen: setGeminiOpen } = useGeminiDrawer()

  // 概要(全カテゴリ)は直近2ヶ月のみ＝「最近保存したもの」を素早く見る。
  // 特定カテゴリ/お気に入りを開いたときは期間を外して全件（limit 300）を表示する。
  const isFocused = !!activeCat || favOnly
  // 日付(YYYY-MM-DD)単位に丸めて安定させる。ミリ秒まで含めると毎レンダーで queryKey が変わり
  // react-query が無限再フェッチして「読み込み中」のままになるため。
  const sinceIso  = isFocused ? undefined : new Date(Date.now() - 60 * 86400000).toISOString().slice(0, 10)
  const { data: items = [], isLoading, error } = useItems({ cat: activeCat, status: 'done', limit: isFocused ? 300 : 500, since: sinceIso, favorite: favOnly })
  const { data: categories = [] } = useCategories()
  const { data: counts = {} } = useCategoryCounts()

  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0)
  const thisWeekCount = items.filter(i => Date.now() - new Date(i.created_at).getTime() < 7 * 86400000).length

  const grouped = activeCat
    ? { [activeCat]: items }
    : categories.reduce<Record<string, typeof items>>((acc, cat) => {
        const ci = items.filter(i => i.cat_id === cat.id)
        if (ci.length > 0) acc[cat.id] = ci
        return acc
      }, {})
  const uncategorized = items.filter(i => !i.cat_id)
  if (uncategorized.length > 0) grouped['memo'] = [...(grouped['memo'] ?? []), ...uncategorized]

  return (
    <div style={{ display:'flex',height:'100dvh',width:'100%',background:T.bg,color:T.text,fontFamily:T.font,overflow:'hidden' }}>
      <Sidebar />
      <div style={{ flex:1,display:'flex',flexDirection:'column',overflow:'hidden' }}>
        <Topbar subtitle={`${totalCount} items`} onGemini={() => setGeminiOpen(true)} />
        <main style={{ flex:1,overflowY:'auto' }}>

          {/* ヒーロー */}
          {!activeCat && items.length > 0 && (
            <section style={{ padding:'20px 22px 0' }}>
              <div style={{ display:'grid',gridTemplateColumns:'1.4fr 1fr',gap:14,marginBottom:18 }}>
                <FeaturedCard item={items[0]} />
                <DigestPanel totalCount={totalCount} thisWeekCount={thisWeekCount} items={items} onAsk={() => setGeminiOpen(true)} />
              </div>
            </section>
          )}


          <div style={{ padding:'6px 22px 40px' }}>
            {isLoading && <div style={{ padding:'40px 0' }}><LoadingSpinner /></div>}
            {error && <ErrorMessage message="データの取得に失敗しました" />}
            {!isLoading && items.length === 0 && <EmptyState />}

            {Object.entries(grouped).map(([catId, catItems]) => {
              const cat = categories.find(c => c.id === catId)
              return (
                <section key={catId} style={{ marginTop:24 }}>
                  <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:12 }}>
                    <CatDot catId={catId} size={10} />
                    <span style={{ fontSize:14,fontWeight:600,color:T.text }}>{cat?.label??CAT_LABELS[catId]??catId}</span>
                    {(() => {
                      const total = counts[catId] ?? catItems.length
                      // 概要では「直近2ヶ月の表示件数 / 全件」。展開時など全件表示なら総数だけ。
                      const label = catItems.length < total ? `${catItems.length} / ${total}` : `${total}`
                      return <span title="直近2ヶ月の表示件数 / 全件" style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute }}>{label} items</span>
                    })()}
                    <button onClick={() => navigate({ to: '/', search: { cat: catId } })} style={{ marginLeft:'auto',background:'transparent',border:'none',color:T.textMute,fontSize:11,fontFamily:T.fontMono,cursor:'pointer',padding:0 }}>すべて表示 →</button>
                  </div>
                  {activeCat ? (
                    // 特定カテゴリを開いた「すべて表示」画面 = 従来の全件グリッド（縦）
                    <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:12 }}>
                      {catItems.map(item => <MosaicCard key={item.id} item={item} />)}
                    </div>
                  ) : (
                    // 概要表示 = 1セクション1行・右スクロール（行数=カテゴリ数のまま）
                    <div style={{ display:'flex',gap:12,overflowX:'auto',paddingBottom:8,scrollbarWidth:'thin' }}>
                      {catItems.map(item => (
                        <div key={item.id} style={{ flex:'0 0 260px',width:260 }}>
                          <MosaicCard item={item} />
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )
            })}
          </div>
        </main>
      </div>

      {/* Gemini ドロワー */}
      <GeminiDrawer open={geminiOpen} onClose={() => setGeminiOpen(false)} />

      {/* フローティング Gemini ボタン */}
      <button
        onClick={() => setGeminiOpen(true)}
        title="Ask the Lake (⌘J)"
        style={{
          position:'fixed',right:24,bottom:24,
          width:48,height:48,borderRadius:24,
          background:'linear-gradient(135deg,oklch(0.72 0.16 258) 0%,oklch(0.60 0.20 278) 100%)',
          border:'1px solid oklch(0.85 0.12 258/0.4)',
          color:'#fff',fontSize:20,cursor:'pointer',
          boxShadow:'0 8px 24px oklch(0.62 0.20 278/0.35)',
          display:'flex',alignItems:'center',justifyContent:'center',
          zIndex:30,
        }}
      >✦</button>
    </div>
  )
}

function FeaturedCard({ item }: { item: any }) {
  const hue = ({tech:210,genai:270,paper:240,news:350,recipe:30,eatery:50,global:180,social:160,medical:130,hobby:320} as any)[item.cat_id] ?? 260
  return (
    <a href={`/items/${item.id}`} style={{ textDecoration:'none' }}>
      <div style={{ background:T.bgRaised,border:`1px solid ${T.border}`,borderRadius:10,overflow:'hidden',display:'flex',flexDirection:'column',cursor:'pointer' }}>
        <div style={{ aspectRatio:'21/9',background:hue===0?'linear-gradient(135deg,#1a1d24 0%,#0e0f14 100%)':`linear-gradient(135deg,oklch(0.28 0.07 ${hue}) 0%,oklch(0.16 0.05 ${hue}) 100%)`,position:'relative',overflow:'hidden' }}>
          <div style={{ position:'absolute',inset:0,backgroundImage:'repeating-linear-gradient(45deg,transparent 0 10px,rgba(255,255,255,0.015) 10px 11px)' }} />
          {hue!==0&&<div style={{ position:'absolute',top:-30,right:-30,width:120,height:120,borderRadius:'50%',background:`oklch(0.72 0.14 ${hue}/0.2)`,filter:'blur(30px)' }} />}
          <div style={{ position:'absolute',top:10,left:14,fontSize:10,fontFamily:T.fontMono,letterSpacing:'0.1em',color:'rgba(255,255,255,0.45)' }}>★ FEATURED</div>
          <div style={{ position:'absolute',bottom:14,left:14,right:14,fontSize:16,fontWeight:700,color:'rgba(255,255,255,0.85)',lineHeight:1.3,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden' }}>
            {item.title??item.original_content.slice(0,80)}
          </div>
        </div>
        <div style={{ padding:16,display:'flex',flexDirection:'column',gap:8 }}>
          <div style={{ display:'flex',alignItems:'center',gap:8,fontSize:11 }}>
            <span style={{ color:catColor(item.cat_id) }}>{CAT_LABELS[item.cat_id??'']??'未分類'}</span>
            {item.source_url&&<><span style={{ color:T.border }}>·</span><span style={{ fontFamily:T.fontMono,color:T.textMute,fontSize:10 }}>{(()=>{try{return new URL(item.source_url).hostname.replace('www.','')}catch{return ''}})()}</span></>}
            <TimeAgo iso={item.created_at} />
          </div>
          {item.summary&&<div style={{ fontSize:12,color:T.textDim,lineHeight:1.6,display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical',overflow:'hidden' }}>{item.summary}</div>}
        </div>
      </div>
    </a>
  )
}

function DigestPanel({ totalCount, thisWeekCount, items, onAsk }: { totalCount:number;thisWeekCount:number;items:any[];onAsk:()=>void }) {
  const catCounts: Record<string,number> = {}
  for (const item of items) { const k=item.cat_id??'memo'; catCounts[k]=(catCounts[k]??0)+1 }
  const topCats = Object.entries(catCounts).sort((a,b)=>b[1]-a[1]).slice(0,4)
  return (
    <div style={{ background:T.bgRaised,border:`1px solid ${T.border}`,borderRadius:10,padding:16,display:'flex',flexDirection:'column',gap:14 }}>
      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
        <span style={{ color:T.accent,fontSize:14 }}>✦</span>
        <span style={{ fontSize:13,fontWeight:600,color:T.text }}>ダイジェスト</span>
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8 }}>
        {[{label:'総アイテム',value:totalCount},{label:'今週追加',value:thisWeekCount}].map(s=>(
          <div key={s.label} style={{ background:T.bgPanel,border:`1px solid ${T.border}`,borderRadius:6,padding:'10px 12px' }}>
            <div style={{ fontSize:9,fontFamily:T.fontMono,color:T.textMute,letterSpacing:'0.1em',marginBottom:4 }}>{s.label}</div>
            <div style={{ fontSize:22,fontWeight:700,fontFamily:T.fontMono,color:T.text,letterSpacing:'-0.03em' }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div>
        <div style={{ fontSize:9,fontFamily:T.fontMono,color:T.textMute,letterSpacing:'0.12em',marginBottom:8 }}>カテゴリ内訳</div>
        <div style={{ display:'flex',flexDirection:'column',gap:5 }}>
          {topCats.map(([catId,count])=>{
            const pct=totalCount?Math.round((count/totalCount)*100):0
            return (
              <div key={catId} style={{ display:'flex',alignItems:'center',gap:8 }}>
                <CatDot catId={catId} size={6} />
                <span style={{ fontSize:11,color:T.textDim,flex:1 }}>{CAT_LABELS[catId]??catId}</span>
                <div style={{ width:60,height:3,background:T.border,borderRadius:2,overflow:'hidden' }}>
                  <div style={{ width:`${pct}%`,height:'100%',background:catColor(catId),borderRadius:2 }} />
                </div>
                <span style={{ fontSize:10,fontFamily:T.fontMono,color:T.textMute,width:24,textAlign:'right' }}>{count}</span>
              </div>
            )
          })}
        </div>
      </div>
      <button onClick={onAsk} style={{ marginTop:'auto',background:T.accentSoft,border:`1px solid ${T.accentBorder}`,color:'oklch(0.85 0.12 258)',borderRadius:6,padding:'8px 12px',fontSize:12,cursor:'pointer',fontFamily:T.font }}>
        ✦ Gemini に聞く
      </button>
    </div>
  )
}

function EmptyState() {
  return (
    <div style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:12,padding:'80px 0',color:T.textMute }}>
      <div style={{ fontSize:32 }}>◇</div>
      <div style={{ fontSize:14,fontWeight:500,color:T.textDim }}>まだアイテムがありません</div>
      <div style={{ fontSize:12,lineHeight:1.7,textAlign:'center',maxWidth:320 }}>
        Discord で <code style={{ fontFamily:T.fontMono,background:T.bgRaised,padding:'1px 6px',borderRadius:3,color:T.accent }}>/memo</code> コマンドを使ってURLやメモを保存してください
      </div>
    </div>
  )
}
